import api from '../../lib/api';

export type ExperimentStatus = 'draft' | 'running' | 'paused' | 'concluded';
export type ExperimentOutcome =
  | 'meeting_booked'
  | 'callback_requested'
  | 'qualified'
  | 'disqualified'
  | 'voicemail'
  | 'no_answer'
  | 'do_not_call'
  | 'other';

export interface VoiceAgentExperiment {
  id: string;
  organizationId: string;
  voiceAgentId: string;
  name: string;
  description: string | null;
  variantAVersionId: string;
  variantBVersionId: string;
  splitPercent: number;
  outcomeTarget: ExperimentOutcome;
  status: ExperimentStatus;
  startedAt: string | null;
  pausedAt: string | null;
  concludedAt: string | null;
  winnerVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExperimentDto {
  organizationId: string;
  name: string;
  description?: string;
  variantAVersionId: string;
  variantBVersionId: string;
  splitPercent: number;
  outcomeTarget?: ExperimentOutcome;
}

export interface UpdateExperimentStatusDto {
  organizationId: string;
  status: ExperimentStatus;
  winnerVersionId?: string;
}

export interface ExperimentReport {
  experimentId: string;
  report: {
    variantA: { totalCalls: number; conversions: number; conversionRate: number };
    variantB: { totalCalls: number; conversions: number; conversionRate: number };
    liftRelative: number;
    pValue: number | null;
    significantAt95: boolean;
    insufficientSample: boolean;
  };
}

const base = (agentId: string) => `/voice-agents/${agentId}/experiments`;

export const experimentsApi = {
  list: (agentId: string, organizationId: string): Promise<VoiceAgentExperiment[]> =>
    api
      .get<VoiceAgentExperiment[]>(base(agentId), { params: { organizationId } })
      .then((r) => r.data),

  create: (agentId: string, dto: CreateExperimentDto): Promise<VoiceAgentExperiment> =>
    api.post<VoiceAgentExperiment>(base(agentId), dto).then((r) => r.data),

  updateStatus: (
    agentId: string,
    experimentId: string,
    dto: UpdateExperimentStatusDto,
  ): Promise<VoiceAgentExperiment> =>
    api
      .patch<VoiceAgentExperiment>(`${base(agentId)}/${experimentId}/status`, dto)
      .then((r) => r.data),

  getReport: (
    agentId: string,
    experimentId: string,
    organizationId: string,
  ): Promise<ExperimentReport> =>
    api
      .get<ExperimentReport>(`${base(agentId)}/${experimentId}/report`, {
        params: { organizationId },
      })
      .then((r) => r.data),
};
